import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import useUser from "../../../hooks/useUser";

const MenuItem = props => {
  const { active = false, href, icon, name, open } = props || {};
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const hideTooltipTimer = useRef(null);

  const queryParams = new URLSearchParams(window.location.search);
  const status = queryParams.get("status");
  const showTooltip = !open && Boolean(name) && Boolean(tooltipPosition);

  useEffect(() => {
    return () => {
      if (hideTooltipTimer.current) {
        clearTimeout(hideTooltipTimer.current);
      }
    };
  }, []);

  const handleMouseEnter = event => {
    if (open || !name) return;
    if (hideTooltipTimer.current) {
      clearTimeout(hideTooltipTimer.current);
      hideTooltipTimer.current = null;
    }
    const bounds = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      left: bounds.right + 1,
      top: bounds.top + bounds.height / 2,
    });
    requestAnimationFrame(() => {
      setTooltipVisible(true);
    });
  };

  const handleMouseLeave = (event) => {
    setTooltipVisible(false);
    hideTooltipTimer.current = setTimeout(() => {
      setTooltipPosition(null);
      hideTooltipTimer.current = null;
    }, 120);
  };

  return (
    <>
      <Link
        className="group flex h-5 cursor-pointer items-center justify-center"
        to={`${href}${status ? `?status=${status}` : ""}`}
        data-title={name}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onBlur={handleMouseLeave}
      >
        {icon && (
          <div
            className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border transition-colors duration-150 ${
              active
                ? "border-black bg-black text-white"
                : "border-transparent text-gray-500 group-hover:border-black group-hover:bg-black group-hover:text-white"
            }`}
          >
            <props.icon className="h-4 w-4" aria-hidden="true" />
          </div>
        )}
        {open && (
          <span
            className={`flex-1 ml-2 text-sm ${active ? "text-black underline" : "text-gray-500"}`}
          >
            {name}
          </span>
        )}
      </Link>
      {showTooltip &&
        createPortal(
          <div
            className={`pointer-events-none fixed z-[100] -translate-y-1/2 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white shadow transition-all duration-150 ${
              tooltipVisible
                ? "opacity-100"
                : "opacity-0"
            }`}
            style={{
              left: tooltipPosition.left,
              top: tooltipPosition.top,
            }}
          >
            {name}
          </div>,
          document.body,
        )}
    </>
  );
};

const NavBar = props => {
  const { navigation = [], open, path } = props || {};
  const location = useLocation();
  const { hasPermission } = useUser();
  const { pathname } = location || {};

  return (
    <div
      className={`flex h-full max-h-full flex-initial flex-col overflow-y-auto bg-white px-2 py-5 no-scrollbar ${
        open ? "ml-3 w-64" : "w-16"
      } min-h-full overflow-x-hidden`}
    >
      <nav className="flex flex-1 flex-col gap-6 bg-white" aria-label="Sidebar">
        {navigation?.map((item, index) => {
          const { permissions } = item;
          const hasPermissions = permissions
            ? permissions?.every?.(permission => hasPermission(permission))
            : true;
          const active = path
            ? path?.includes(item?.href)
            : pathname?.split("/")?.pop() == item?.href ||
              pathname?.split("/")?.[2] == item?.href; // Admin Module User and Roles
          return (
            hasPermissions && (
              <MenuItem key={index} {...item} open={open} active={active} />
            )
          );
        })}
      </nav>
    </div>
  );
};

const SimpleSidebar = props => {
  const { open, setOpen } = props || {};
  const [hasSidebarEntries, setHasSidebarEntries] = useState(false);
  const sidebarTargetRef = useRef(null);

  useEffect(() => {
    if (!sidebarTargetRef.current) return undefined;

    const updateHasEntries = () => {
      setHasSidebarEntries(sidebarTargetRef.current?.children?.length > 0);
    };

    updateHasEntries();

    const observer = new MutationObserver(updateHasEntries);
    observer.observe(sidebarTargetRef.current, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="flex h-screen max-h-screen flex-col overflow-hidden border-r border-gray-200">
      <div
        className={
          `flex items-center rounded-md bg-white p-5 text-gray-400 ${
            hasSidebarEntries
              ? "cursor-pointer hover:bg-gray-100 hover:text-gray-500"
              : "cursor-default"
          }` +
          "focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        }
        onClick={() => {
          if (hasSidebarEntries) {
            setOpen(!open);
          }
        }}
      >
        <span className="sr-only">Open side bar</span>
        {hasSidebarEntries ? (
          open ? (
            <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
          ) : (
            <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
          )
        ) : (
          <span className="block h-6 w-6" aria-hidden="true" />
        )}
      </div>
      <div
        id="sidebar"
        ref={sidebarTargetRef}
        className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden no-scrollbar"
      />
    </div>
  );
};

export { NavBar, SimpleSidebar };
